import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { EmptyState, ErrorState, Field, Modal, Spinner } from '../../components/ui.jsx';
import { ChargeTable, LedgerSummary, PaymentTable } from '../../components/lease.jsx';
import { api, ApiError } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useMeta } from '../../state/MetaContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { formatDate, formatXaf, rentPeriodNoun } from '../../lib/format.js';

// How often to ask the provider whether the payer has approved the prompt yet.
const POLL_MS = 4000;

const PAYOUT_ACCOUNT_LABELS = {
  mtn_momo: 'MTN Mobile Money',
  orange_money: 'Orange Money',
  bank_transfer: 'Bank transfer',
  other: 'Other'
};

// The rent menu keeps the three obligations apart: the house rent on its lease
// cycle, internet month by month, and the one-off refundable deposit. Each is
// paid separately, always against a specific charge, so a receipt never says
// "rent" when the money was for internet.
const CATEGORIES = [
  {
    kind: 'rent',
    title: 'House rent',
    hint: (tenancy, billingLabel) =>
      `Billed ${billingLabel.toLowerCase()} from ${formatDate(tenancy.startDate)}, the date you moved in.`
  },
  {
    kind: 'internet',
    title: 'Internet',
    hint: () =>
      'Due on the 27th of every month. 5% late fee after 5 days late; service is cut off after 15 days late.'
  },
  {
    kind: 'deposit',
    title: 'Security deposit',
    hint: () => 'Paid once. Refundable when your tenancy ends without damage to the property.'
  },
  {
    kind: 'electricity',
    title: 'Electricity',
    hint: () => 'Bills your landlord has added for electricity, paid separately from rent.'
  },
  {
    kind: 'water',
    title: 'Water',
    hint: () => 'Bills your landlord has added for water, paid separately from rent.'
  },
  {
    kind: 'other',
    title: 'Other charges',
    hint: () => 'Late fees and one-off bills your landlord has added.'
  }
];

const categorise = (charges) => {
  const groups = { rent: [], internet: [], deposit: [], electricity: [], water: [], other: [] };
  for (const charge of charges) {
    if (charge.status === 'waived') continue;
    (groups[charge.kind] ?? groups.other).push(charge);
  }
  return CATEGORIES.map((category) => {
    const list = groups[category.kind];
    const open = list.filter((charge) => charge.outstanding > 0);
    return {
      ...category,
      charges: list,
      outstanding: open.reduce((sum, charge) => sum + charge.outstanding, 0),
      overdue: open.some((charge) => charge.overdue),
      // Charges arrive ordered by due date, so the first open one is the oldest.
      nextCharge: open[0] ?? null
    };
  });
};

const PAY_TITLES = {
  rent: 'Pay your house rent',
  internet: 'Pay your internet',
  deposit: 'Pay your deposit',
  electricity: 'Pay your electricity bill',
  water: 'Pay your water bill',
  penalty: 'Pay a late fee',
  utility: 'Pay a bill'
};

/**
 * Mobile money payment. Creating an intent asks the provider to collect; the tenant
 * then approves it on their handset (MTN) or on a hosted page (Orange). We poll
 * until the provider gives a verdict, and only a confirmed collection becomes a
 * payment on the statement.
 */
const PayModal = ({ tenancy, charge, phone, momoMode, onClose, onPaid }) => {
  const toast = useToast();
  const { momoProviders } = useMeta();

  const outstanding = charge?.outstanding ?? Math.max(tenancy.ledger.totals.balance, 0);
  const [form, setForm] = useState({
    provider: 'mtn_momo',
    amount: String(outstanding || ''),
    phone: phone || ''
  });
  const [errors, setErrors] = useState({});
  const [intent, setIntent] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      const result = await api.post(`/tenancies/${tenancy.id}/payment-intents`, {
        provider: form.provider,
        amount: Number(form.amount),
        chargeId: charge?.id ?? null,
        phone: form.phone.trim()
      });
      setIntent({ ...result.intent, instruction: result.instruction });

      // Orange hands back a hosted page rather than pushing a prompt.
      if (result.intent.redirectUrl) window.open(result.intent.redirectUrl, '_blank', 'noopener');
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const poll = useCallback(async () => {
    if (!intent) return;
    if (intent.status !== 'pending' && !(intent.status === 'succeeded' && !intent.paymentId)) return;
    try {
      const result = await api.get(`/payment-intents/${intent.id}`);
      setIntent((current) => ({ ...current, ...result.intent }));

      if (result.intent.status === 'succeeded' && result.intent.paymentId) {
        toast.success('Payment confirmed by your landlord. Your receipt is ready.');
        onPaid();
      } else if (result.intent.status === 'succeeded' && !result.intent.paymentId && intent.status === 'pending') {
        toast.info('Provider approved it. Waiting for your landlord to confirm receipt.');
      }
      if (result.intent.status === 'failed') {
        toast.error(result.intent.failureReason || 'The payment did not go through.');
      }
    } catch {
      // A hiccup while polling is not worth shouting about; the next tick retries.
    }
  }, [intent, toast, onPaid]);

  useEffect(() => {
    if (!intent || intent.status !== 'pending') return undefined;
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [intent, poll]);

  const cancel = async () => {
    if (intent?.status === 'pending') {
      await api.post(`/payment-intents/${intent.id}/cancel`).catch(() => {});
    }
    onClose();
  };

  return (
    <Modal
      title={PAY_TITLES[charge?.kind] ?? 'Pay your rent'}
      onClose={cancel}
      footer={
        intent ? (
          <>
            <button type="button" className="btn btn-ghost" onClick={cancel}>
              {intent.status === 'pending' ? 'Give up on this attempt' : 'Close'}
            </button>
            {intent.status === 'pending' || (intent.status === 'succeeded' && !intent.paymentId) ? (
              <button type="button" className="btn btn-outline" onClick={poll}>
                Check status now
              </button>
            ) : null}
            {intent.status === 'failed' ? (
              <button type="button" className="btn btn-primary" onClick={() => setIntent(null)}>
                Try again
              </button>
            ) : null}
          </>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={cancel} disabled={busy}>
              Cancel
            </button>
            <button type="submit" form="pay-form" className="btn btn-primary" disabled={busy}>
              {busy ? 'Asking the provider...' : `Pay ${formatXaf(Number(form.amount) || 0)}`}
            </button>
          </>
        )
      }
    >
      {momoMode === 'mock' ? (
        <div className="notice notice-info">
          <strong>Demonstration mode.</strong> This goes through the whole payment flow and issues a real receipt, but no
          actual money moves. A number ending in 9 is treated as a refusal, so you can see both outcomes.
        </div>
      ) : null}

      {!intent ? (
        <form id="pay-form" className="stack" onSubmit={start}>
          <p className="modal-lead">
            {charge ? `Paying "${charge.label}".` : 'This goes against the oldest thing you owe.'}{' '}
            {formatXaf(outstanding)} is outstanding.
          </p>

          <Field label="Pay with" error={errors.provider} required>
            <div className="provider-choice">
              {momoProviders.map((provider) => (
                <button
                  key={provider.slug}
                  type="button"
                  className={`provider-option ${form.provider === provider.slug ? 'is-current' : ''}`}
                  onClick={() => setForm((current) => ({ ...current, provider: provider.slug }))}
                >
                  {provider.label}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid-2">
            <Field label="How much, in FCFA" error={errors.amount} required>
              <input
                type="number"
                min="1"
                max={charge ? charge.outstanding : undefined}
                step="1"
                value={form.amount}
                onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
                required
              />
            </Field>

            <Field label="Number to charge" error={errors.phone} required>
              <input
                type="tel"
                value={form.phone}
                onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
                placeholder="+237 6 70 00 00 00"
                required
              />
            </Field>
          </div>

          <p className="field-hint">
            You can pay part of what you owe. Your landlord confirms receipt before it is posted to your statement.
          </p>
        </form>
      ) : (
        <div className="pay-status">
          {intent.status === 'pending' ? (
            <>
              <div className="pay-spinner" aria-hidden="true" />
              <h3>Waiting for you to approve it</h3>
              <p>{intent.instruction}</p>
              <p className="muted">
                {formatXaf(intent.amount)} on {intent.payerPhone}. This page checks every few seconds.
              </p>
              {intent.redirectUrl ? (
                <a className="btn btn-outline" href={intent.redirectUrl} target="_blank" rel="noreferrer noopener">
                  Open the payment page again
                </a>
              ) : null}
            </>
          ) : null}

          {intent.status === 'succeeded' && !intent.paymentId ? (
            <>
              <div className="pay-tick" aria-hidden="true">
                &#10003;
              </div>
              <h3>Awaiting landlord confirmation</h3>
              <p>
                The provider approved {formatXaf(intent.amount)}. Your landlord must confirm receipt before this payment
                appears on your statement and receipts.
              </p>
            </>
          ) : null}

          {intent.status === 'succeeded' && intent.paymentId ? (
            <>
              <div className="pay-tick" aria-hidden="true">
                &#10003;
              </div>
              <h3>Paid</h3>
              <p>
                {formatXaf(intent.amount)} received. Your receipt is on your statement, and your landlord has been
                notified.
              </p>
            </>
          ) : null}

          {['failed', 'cancelled', 'expired'].includes(intent.status) ? (
            <>
              <div className="pay-cross" aria-hidden="true">
                &times;
              </div>
              <h3>It did not go through</h3>
              <p>{intent.failureReason || 'The payment was not completed.'}</p>
              <p className="muted">Nothing has been taken and nothing has been added to your statement.</p>
            </>
          ) : null}
        </div>
      )}
    </Modal>
  );
};

const MyRent = () => {
  useTitle('My rent');
  const toast = useToast();
  const { billingPeriods } = useMeta();
  const { data, loading, error, reload } = useFetch('/tenant/rent');
  // Utilities are a separate book, so what is owed on the meter would otherwise
  // be invisible from here. A pointer, not a merge: the two ledgers stay apart.
  const utilities = useFetch('/me/utilities');
  const [paying, setPaying] = useState(null);
  const [respondingId, setRespondingId] = useState(null);

  const utilitiesOwed = (utilities.data?.items || []).reduce(
    (total, item) => total + (item.totals?.balance || 0),
    0
  );

  const respondToInternetOffer = async (tenancy, accept) => {
    setRespondingId(tenancy.id);
    try {
      await api.post(`/tenancies/${tenancy.id}/internet-offer/respond`, { accept });
      toast.success(accept ? 'Internet accepted. Billing starts today.' : 'Offer declined.');
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setRespondingId(null);
    }
  };

  if (loading && !data) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const items = data.items || [];

  if (items.length === 0) {
    return (
      <div className="container section">
        <EmptyState
          title="No rent account yet"
          message="Once your lease is signed by both you and your landlord, your rent schedule and receipts appear here."
          action={
            <Link to="/tenant/lease" className="btn btn-primary">
              See my lease
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>My rent</h1>
          <p className="muted">What you owe, what you have paid, and every receipt.</p>
        </div>
        <Link to="/tenant/lease" className="btn btn-ghost">
          My lease
        </Link>
      </header>

      {utilitiesOwed > 0 ? (
        <div className="notice notice-warn">
          <strong>{formatXaf(utilitiesOwed)} is also owing on your utilities.</strong> Light, water and internet are
          billed off your landlord's meter and kept separately from rent.{' '}
          <Link to="/my/utilities">See what you owe</Link>.
        </div>
      ) : null}

      {data.pendingIntent?.status === 'pending' ? (
        <div className="notice notice-warn">
          <strong>Payment in progress.</strong> Your phone prompt is still pending. Open <strong>Pay rent</strong> to check
          status.
        </div>
      ) : null}

      {data.pendingIntent?.status === 'succeeded' && !data.pendingIntent?.paymentId ? (
        <div className="notice notice-info">
          <strong>Payment waiting for landlord confirmation.</strong> The provider approved{' '}
          {formatXaf(data.pendingIntent.amount)}, but it will appear in your statement only after your landlord confirms
          receipt.
        </div>
      ) : null}

      {items.map((tenancy) => {
        const { ledger } = tenancy;
        const nextDue = ledger.totals.nextDue;
        const billingLabel =
          billingPeriods.find((period) => period.slug === tenancy.billingPeriod)?.label || 'every period';
        const categories = categorise(ledger.charges).filter(
          (category) =>
            category.charges.length > 0 ||
            (category.kind === 'rent') ||
            (category.kind === 'internet' && tenancy.internet > 0) ||
            (category.kind === 'deposit' && tenancy.deposit > 0)
        );

        return (
          <section key={tenancy.id} className="lease-panel">
            <header className="lease-panel-head">
              <div>
                <h2>
                  {tenancy.unit?.label ? `${tenancy.unit.label}, ` : ''}
                  {tenancy.property?.title}
                </h2>
                <p className="muted">
                  {formatXaf(tenancy.rent)} a {rentPeriodNoun(tenancy.billingPeriod)}, due on day{' '}
                  {tenancy.rentDueDay}. Landlord: {tenancy.landlord?.name}
                  {tenancy.landlord?.phone ? (
                    <>
                      {' - '}
                      <a href={`tel:${tenancy.landlord.phone}`}>{tenancy.landlord.phone}</a>
                    </>
                  ) : null}
                </p>
              </div>
              <Link to={`/leases/${tenancy.id}/full-receipt`} className="btn btn-outline btn-sm">
                Full receipt
              </Link>
            </header>

            {tenancy.landlordPaymentAccounts?.length > 0 ? (
              <div className="notice notice-info">
                <strong>Where to pay:</strong>
                <ul className="payout-account-list">
                  {tenancy.landlordPaymentAccounts.map((account, index) => (
                    <li key={index}>
                      {PAYOUT_ACCOUNT_LABELS[account.kind] || account.kind}
                      {account.label ? ` - ${account.label}` : ''}: <strong>{account.accountNumber}</strong> (
                      {account.accountName}
                      {account.bankName ? `, ${account.bankName}` : ''})
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {tenancy.internetOffer ? (
              <div className="notice notice-warn">
                <strong>
                  Your landlord is offering internet at {formatXaf(tenancy.internetOffer)} a month.
                </strong>{' '}
                Nothing is billed unless you accept. Billing would start today, due on the 27th of every month.
                <div className="row-actions" style={{ marginTop: '0.75rem' }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => respondToInternetOffer(tenancy, true)}
                    disabled={respondingId === tenancy.id}
                  >
                    {respondingId === tenancy.id ? 'Saving...' : 'Accept'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => respondToInternetOffer(tenancy, false)}
                    disabled={respondingId === tenancy.id}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ) : null}

            <div className="pay-category-row">
              {categories.map((category) => (
                <article
                  key={category.kind}
                  className={`pay-category ${category.overdue ? 'is-overdue' : category.outstanding === 0 ? 'is-clear' : ''}`}
                >
                  <h3>{category.title}</h3>
                  <span className="pay-category-amount">
                    {category.outstanding > 0 ? formatXaf(category.outstanding) : 'Paid up'}
                  </span>
                  {category.nextCharge ? (
                    <span className="pay-category-due">
                      {category.overdue ? 'Overdue: ' : 'Next: '}
                      {category.nextCharge.label}, due {formatDate(category.nextCharge.dueDate)}
                    </span>
                  ) : null}
                  <p className="pay-category-hint">{category.hint(tenancy, billingLabel)}</p>
                  {tenancy.status === 'active' && category.nextCharge ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setPaying({ tenancy, charge: category.nextCharge })}
                    >
                      Pay {formatXaf(category.nextCharge.outstanding)}
                    </button>
                  ) : null}
                </article>
              ))}
            </div>

            <LedgerSummary totals={ledger.totals} />

            {ledger.totals.overdue > 0 ? (
              <div className="notice notice-bad">
                <strong>{formatXaf(ledger.totals.overdue)} is overdue.</strong> Pay what you can, or speak to your
                landlord. Every payment is receipted, so you always have proof.
              </div>
            ) : nextDue ? (
              <div className="notice notice-info">
                Next due: <strong>{nextDue.label}</strong>, {formatXaf(nextDue.outstanding)} by{' '}
                {formatDate(nextDue.dueDate)}.
              </div>
            ) : (
              <div className="notice notice-good">Your rent account is clear. Nothing is owing.</div>
            )}

            <section className="section-block">
              <div className="section-head">
                <h3>What you have been charged</h3>
              </div>
              <ChargeTable
                charges={ledger.charges}
                payLabel="Pay this"
                onPay={tenancy.status === 'active' ? (charge) => setPaying({ tenancy, charge }) : undefined}
              />
            </section>

            <section className="section-block">
              <div className="section-head">
                <h3>My receipts</h3>
                <span className="muted">Keep these. They are your proof of payment.</span>
              </div>
              <PaymentTable payments={ledger.payments} />
            </section>
          </section>
        );
      })}

      {paying ? (
        <PayModal
          tenancy={paying.tenancy}
          charge={paying.charge}
          phone={data.me?.phone}
          momoMode={data.momoMode}
          onClose={() => setPaying(null)}
          onPaid={() => {
            reload();
          }}
        />
      ) : null}
    </div>
  );
};

export default MyRent;
